import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, API, notifyError } from "@/lib/api";
import { Certificate, Plus, X, Copy, CheckCircle, ArrowSquareOut, Prohibit, ShieldCheck, Code } from "@phosphor-icons/react";

export default function Certificates() {
  const [certs, setCerts] = useState([]);
  const [grants, setGrants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ grant_id: "", org_name: "", valid_days: 90 });
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [embedFor, setEmbedFor] = useState(null); // cert object

  const load = useCallback(async () => {
    const [c, g] = await Promise.all([api.get("/certificates"), api.get("/grants")]);
    setCerts(c.data); setGrants(g.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/certificates", {
        grant_id: form.grant_id || null,
        org_name: form.org_name || null,
        valid_days: Number(form.valid_days || 90),
      });
      setOpen(false);
      setForm({ grant_id: "", org_name: "", valid_days: 90 });
      load();
    } catch (err) { notifyError(err); }
    finally { setSaving(false); }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this certificate? The public URL will stop working.")) return;
    await api.delete(`/certificates/${id}`); load();
  };

  const shareUrl = (t) => `${window.location.origin}/certificate/${t}`;

  const copy = async (id, token) => {
    try { await navigator.clipboard.writeText(shareUrl(token)); }
    catch { window.prompt("Copy link:", shareUrl(token)); }
    setCopiedId(id); setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Compliance Certificates</div>
          <h1 className="font-display text-4xl">Prove compliance instantly</h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Issue a signed, tamper-evident certificate to send to Enterprise Ireland, Arc Hub, Horizon Europe reviewers or investors.
            Every certificate carries an <span className="mono text-white/80">audit hash</span> derived from your live compliance data.
          </p>
        </div>
        <button data-testid="btn-new-cert" onClick={() => setOpen(true)} className="btn-gold"><Plus weight="bold" size={16}/> Issue certificate</button>
      </div>

      {certs.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <Certificate size={40} weight="duotone" color="#FFB020" className="mx-auto mb-3"/>
          <div className="font-display text-xl">No certificates yet</div>
          <div className="text-white/50 text-sm mt-2">Issue your first compliance certificate — a public URL you can share with grant bodies.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {certs.map((c,i) => {
            const expired = new Date(c.expires_at) < new Date();
            let status = "active";
            if (c.revoked) status = "revoked";
            else if (expired) status = "expired";
            let badgeClass = "badge-fail";
            if (status === "active") badgeClass = "badge-pass";
            else if (status === "expired") badgeClass = "badge-warn";
            const scope = c.payload?.scope || {};
            return (
              <motion.div key={c.cert_id} initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay: i*0.05}}
                data-testid={`cert-card-${c.cert_id}`}
                className="glass rounded-2xl p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{scope.type === "single_grant" ? scope.programme : "Organisation-wide"}</div>
                    <div className="font-display text-lg mt-1">{scope.type === "single_grant" ? scope.grant_name : c.payload?.org_name}</div>
                  </div>
                  <span className={`${badgeClass} px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold`}>{status}</span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg bg-black/25 border border-white/5 p-3">
                    <div className="text-[9px] uppercase tracking-wider text-white/45">Score</div>
                    <div className="mono text-lg mt-1" style={{ color: "#00A86B" }}>{c.payload?.compliance_score ?? 100}%</div>
                  </div>
                  <div className="rounded-lg bg-black/25 border border-white/5 p-3">
                    <div className="text-[9px] uppercase tracking-wider text-white/45">Spent</div>
                    <div className="mono text-lg mt-1" style={{ color: "#FFB020" }}>€{Number(scope.total_spent||0).toLocaleString()}</div>
                  </div>
                  <div className="rounded-lg bg-black/25 border border-white/5 p-3">
                    <div className="text-[9px] uppercase tracking-wider text-white/45">Views</div>
                    <div className="mono text-lg mt-1">{c.view_count || 0}</div>
                  </div>
                </div>

                <div className="text-xs text-white/50 mono flex flex-wrap gap-x-4 gap-y-1">
                  <span>Hash: <span className="text-white/80">{c.audit_hash}</span></span>
                  <span>Issued: {c.issued_at?.slice(0,10)}</span>
                  <span>Expires: {c.expires_at?.slice(0,10)}</span>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
                  <button data-testid={`btn-copy-${c.cert_id}`} onClick={() => copy(c.cert_id, c.share_token)} className="btn-ghost text-xs" disabled={c.revoked || expired}>
                    {copiedId === c.cert_id ? <><CheckCircle size={13} weight="fill" color="#00A86B"/> Copied</> : <><Copy size={13}/> Copy public link</>}
                  </button>
                  <a href={shareUrl(c.share_token)} target="_blank" rel="noreferrer" className={`btn-ghost text-xs ${c.revoked || expired ? "pointer-events-none opacity-40" : ""}`} data-testid={`btn-view-${c.cert_id}`}>
                    <ArrowSquareOut size={13}/> View
                  </a>
                  <button data-testid={`btn-embed-${c.cert_id}`} onClick={() => setEmbedFor(c)} className="btn-ghost text-xs" disabled={c.revoked || expired}>
                    <Code size={13}/> Get embed
                  </button>
                  {!c.revoked && !expired && (
                    <button onClick={() => revoke(c.cert_id)} className="btn-danger text-xs" data-testid={`btn-revoke-${c.cert_id}`}>
                      <Prohibit size={13}/> Revoke
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {open && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
            <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}}
              className="glass rounded-2xl w-full max-w-xl my-8 p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="eyebrow mb-1">Issue certificate</div>
                  <div className="font-display text-2xl">New Compliance Certificate</div>
                </div>
                <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white" data-testid="btn-close-cert-modal"><X size={20}/></button>
              </div>

              <div className="rounded-xl bg-[#FFB020]/8 border border-[#FFB020]/25 p-4 mb-5 text-xs text-white/70 flex gap-3">
                <ShieldCheck size={18} weight="duotone" color="#FFB020" className="shrink-0 mt-0.5"/>
                <span>
                  We&apos;ll snapshot your compliance data now and generate a public URL. The audit hash proves the data hasn&apos;t changed.
                  You can revoke the certificate anytime.
                </span>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label>Scope</label>
                  <select value={form.grant_id} onChange={e => setForm({...form, grant_id: e.target.value})} data-testid="select-cert-scope">
                    <option value="">Organisation-wide (all grants)</option>
                    {grants.map(g => <option key={g.grant_id} value={g.grant_id}>{g.programme} — {g.name}</option>)}
                  </select>
                </div>
                <div>
                  <label>Organisation name (optional)</label>
                  <input value={form.org_name} onChange={e => setForm({...form, org_name: e.target.value})} placeholder="PerCen Technologies Ltd" data-testid="input-cert-org"/>
                </div>
                <div>
                  <label>Valid for (days)</label>
                  <input type="number" min={1} max={365} value={form.valid_days} onChange={e => setForm({...form, valid_days: e.target.value})} data-testid="input-cert-days"/>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                  <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
                  <button type="submit" disabled={saving} className="btn-gold" data-testid="btn-issue-cert">
                    {saving ? "Issuing…" : "Issue certificate"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {embedFor && <EmbedModal cert={embedFor} onClose={() => setEmbedFor(null)} />}
      </AnimatePresence>
    </div>
  );
}

function EmbedModal({ cert, onClose }) {
  const [copied, setCopied] = useState("");
  const origin = window.location.origin;
  const publicUrl = `${origin}/certificate/${cert.share_token}`;
  const badgeUrl = `${API}/public/certificate/${cert.share_token}/badge.svg`;
  const iframeUrl = `${origin}/embed/certificate/${cert.share_token}`;

  const markdown = `[![PerCen360 Verified](${badgeUrl})](${publicUrl})`;
  const html = `<a href="${publicUrl}" target="_blank" rel="noopener"><img src="${badgeUrl}" alt="PerCen360 Verified — Compliance ${cert.payload?.compliance_score}%" /></a>`;
  const iframe = `<iframe src="${iframeUrl}" width="380" height="120" frameborder="0" style="border:0;border-radius:12px" title="PerCen360 Certificate"></iframe>`;

  const copy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); }
    catch { window.prompt("Copy:", text); }
    setCopied(key); setTimeout(() => setCopied(""), 2000);
  };

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}}
        className="glass rounded-2xl w-full max-w-2xl my-8 p-8" data-testid="embed-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="eyebrow mb-1">Embed this certificate</div>
            <div className="font-display text-2xl">Paste anywhere</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="btn-close-embed-modal"><X size={20}/></button>
        </div>

        <div className="rounded-xl bg-[#FFB020]/8 border border-[#FFB020]/25 p-4 mb-6 text-xs text-white/70">
          Live badges update automatically. Every click routes viewers to your public certificate — a warm inbound signal for grant bodies and investors.
        </div>

        {/* Preview */}
        <div className="rounded-xl bg-black/40 border border-white/10 p-6 mb-6 flex items-center justify-center gap-6 flex-wrap">
          <img src={badgeUrl} alt="Live badge preview" style={{ height: 28 }} data-testid="badge-preview"/>
          <iframe src={iframeUrl} width="380" height="120" style={{ border: 0, borderRadius: 12 }} title="Embed preview" data-testid="iframe-preview" />
        </div>

        <div className="space-y-4">
          <Snippet
            label="Markdown (READMEs, GitHub, Notion)"
            code={markdown}
            copied={copied === "md"}
            onCopy={() => copy("md", markdown)}
            testId="snippet-markdown"
          />
          <Snippet
            label="HTML badge (email signatures, pitch decks exported as HTML)"
            code={html}
            copied={copied === "html"}
            onCopy={() => copy("html", html)}
            testId="snippet-html"
          />
          <Snippet
            label="Rich iframe widget (Webflow, Wix, Squarespace, custom site)"
            code={iframe}
            copied={copied === "iframe"}
            onCopy={() => copy("iframe", iframe)}
            testId="snippet-iframe"
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

function Snippet({ label, code, copied, onCopy, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs text-white/60">{label}</label>
        <button onClick={onCopy} className="btn-ghost text-xs" data-testid={testId}>
          {copied ? <><CheckCircle size={12} weight="fill" color="#00A86B"/> Copied</> : <><Copy size={12}/> Copy</>}
        </button>
      </div>
      <pre className="mono text-[11px] bg-black/40 border border-white/10 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all text-white/80 select-all">{code}</pre>
    </div>
  );
}
